import { yupResolver } from "@hookform/resolvers/yup";
import { Stack, TextField, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { isValidPhoneNumber } from "react-phone-number-input";
import * as yup from "yup";
import AutomatedMessageInfo from "@/components/booking/automatedMessageInfo";
import MarketingConsent from "@/components/booking/marketingConsent";
import LoadingButton from "@/components/common/buttons/loadingButton";
import { ConfirmationCheckboxes } from "@/components/common/checkbox/confirmationCheckboxes";
import { useClientBookingContext } from "@/contexts/clientBookingContext";
import useAction from "@/hooks/misc/useAction";
import { ClientDetailsInput } from "@/types/bookingTypes";
import { FormPhoneField } from "../../serviceFlow/clientDetails/forms/formFields";
import AgreementCheckbox from "../agreementCheckbox";
import BookingInfoCard from "../bookingInfoCard";

const schema = yup.object().shape({
  firstName: yup.string().required("Enter your first name."),
  lastName: yup.string().required("Enter your last name."),
  email: yup
    .string()
    .email("Enter the valid e-mail in the the format: example@domain.com")
    .required("Enter the valid e-mail in the the format: example@domain.com"),
  phone: yup
    .string()
    .test((value: string) => {
      return isValidPhoneNumber(value);
    })
    .required(),
  comment: yup.string(),
  ageConsent: yup
    .boolean()
    .isTrue(
      "Confirm your age and consent to the Privacy and Telehealth terms."
    ),
  marketingConsent: yup
    .boolean()
    .isTrue("Please consent to the marketing terms."),
});

export default function ClientDetails({
  onSubmit,
  collectCardInformation,
}: {
  onSubmit: (data: ClientDetailsInput) => Promise<unknown>;
  collectCardInformation: boolean;
}) {
  const { clientDetails, medspaData } = useClientBookingContext();
  const formApi = useForm<ClientDetailsInput>({
    resolver: yupResolver(schema),
    defaultValues: clientDetails || {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      comment: "",
      ageConsent: false,
      marketingConsent: false,
    },
    mode: "onTouched",
  });

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    formState: { errors, isSubmitting },
  } = formApi;

  const { handleAction, isLoading } = useAction(onSubmit);

  const handleBookingSubmit = () => {
    handleSubmit(() => handleAction(getValues()))();
  };

  const isFinalBookingStep = !collectCardInformation;

  const checkboxesToWatch: (keyof ClientDetailsInput)[] = [
    "marketingConsent",
    "ageConsent",
  ];

  return (
    <BookingInfoCard>
      <Typography
        variant="h3"
        sx={{
          color: "text.primary",
          pb: 3,
        }}
      >
        Enter your details below
      </Typography>
      <form style={{ width: "100%" }}>
        <Stack spacing={3}>
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <TextField
                type="text"
                placeholder="First name"
                {...field}
                error={!!errors.firstName}
                helperText={
                  errors.firstName ? (errors.firstName.message as string) : ""
                }
              />
            )}
          />

          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <TextField
                type="text"
                placeholder="Last name"
                {...field}
                error={!!errors.lastName}
                helperText={
                  errors.lastName ? (errors.lastName.message as string) : ""
                }
              />
            )}
          />

          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                type="email"
                placeholder="Email"
                {...field}
                error={!!errors.email}
                helperText={
                  errors.email ? (errors.email.message as string) : ""
                }
              />
            )}
          />

          <FormPhoneField<ClientDetailsInput>
            control={control}
            name="phone"
            setValue={setValue}
            readOnly
          />

          <Controller
            name="comment"
            control={control}
            render={({ field }) => (
              <TextField
                multiline
                minRows={3}
                slotProps={{
                  htmlInput: { maxLength: 300 },
                }}
                type="visitReason"
                placeholder="Visit reason"
                {...field}
                error={!!errors.comment}
                helperText={
                  errors.comment ? (errors.comment.message as string) : ""
                }
              />
            )}
          />

          {isFinalBookingStep && (
            <>
              <ConfirmationCheckboxes
                getValues={getValues}
                setValue={setValue}
                checkboxNames={checkboxesToWatch}
              >
                <MarketingConsent
                  name="marketingConsent"
                  control={control}
                  errors={errors}
                />

                <AgreementCheckbox
                  control={control}
                  name={"ageConsent"}
                  medspaId={medspaData.id}
                  errors={errors}
                />
              </ConfirmationCheckboxes>
            </>
          )}
        </Stack>

        <LoadingButton
          fullWidth
          sx={{ mt: 3 }}
          variant="contained"
          onClick={handleBookingSubmit}
          disabled={!!isSubmitting}
          loading={isLoading}
        >
          {isFinalBookingStep ? "Book appointment" : "Next"}
        </LoadingButton>
      </form>
      {isFinalBookingStep && <AutomatedMessageInfo />}
    </BookingInfoCard>
  );
}
